import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { useSelector } from "react-redux";
// @mui
import {
  Card,
  Stack,
  Button,
  Container,
  Typography,
  CircularProgress,
} from "@mui/material";

// redux
import { Edit } from "@mui/icons-material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import {
  useDeleteSupportedDeviceMutation,
  useGetSupportedDevicesQuery,
  useLazyGetSupportedDevicesQuery,
} from "../redux/services/supportedDevice.service";
import useDownloadAll from "../hooks/useDownloadAll";

// components
import Iconify from "../components/iconify";
import Scrollbar from "../components/scrollbar";
import Protected from "../protected/Protected";
// sections
import SupportedDeviceForm from "../sections/@dashboard/supported-device/supportedDevice.form";
import CustomTable from "../components/data-table/CustomDataTable";
import { selectAbility } from "../redux/slices/casl.slice";
import CustomDeleteConfirmation from "../components/dialog/CustomDeleteConfirmation";

export default function SupportedDevicePage() {
  const ability = useSelector(selectAbility);

  const { handleDownloadAll, isDownloading } = useDownloadAll(
    useLazyGetSupportedDevicesQuery,
    "SupportedDevices",
  );

  const {
    data: supportedDevices,
    isLoading: isSupportedDevicesLoading,
    isFetching: isSupportedDevicesFetching,
  } = useGetSupportedDevicesQuery();

  const [deleteSupportedDevice, { isLoading: isDeleting }] =
    useDeleteSupportedDeviceMutation();

  const [openSupportedDeviceForm, setOpenSupportedDeviceForm] = useState(false);

  const [edit, setEdit] = useState(false);

  const [openRow, setOpenRow] = useState({});

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const handleCloseFilter = () => {
    setOpenSupportedDeviceForm(false);
    setEdit(false);
  };

  return (
    <Protected>
      <Helmet>
        <title> Supported Devices </title>
      </Helmet>

      <Container>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Supported Device
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <Button
              variant="outlined"
              startIcon={
                isDownloading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Iconify icon="eva:download-fill" />
                )
              }
              onClick={() => handleDownloadAll()}
              disabled={isDownloading}
            >
              Download All
            </Button>
            {ability.can("post", "Supported-Device") && (
              <Button
                variant="contained"
                onClick={setOpenSupportedDeviceForm}
                startIcon={<Iconify icon="eva:plus-fill" />}
              >
                New Supported Devices
              </Button>
            )}
          </Stack>
          {(ability.can("post", "Supported-Device") ||
            ability.can("patch", "Supported-Device")) && (
            <SupportedDeviceForm
              open={openSupportedDeviceForm}
              onClose={handleCloseFilter}
              edit={edit}
              openRow={openRow}
              deviceOptions={supportedDevices?.data}
              isLoading={
                isSupportedDevicesLoading || isSupportedDevicesFetching
              }
            />
          )}
        </Stack>

        <Card>
          <Scrollbar>
            <CustomTable
              canSelect
              useDataQuery={useGetSupportedDevicesQuery}
              tableName="Supported Devices"
              searchLabel="Search Device..."
              searchCols={["deviceName"]}
              actions={
                ability.can("patch", "Supported-Device") ||
                ability.can("delete", "Supported-Device")
                  ? {
                      field: "actions",
                      headerName: "Action",
                      type: "actions",
                      getActions: ({ row }) =>
                        [
                          ability.can("patch", "Supported-Device") ? (
                            <GridActionsCellItem
                              key="edit"
                              icon={<Edit />}
                              label="Edit"
                              showInMenu
                              onClick={() => {
                                setOpenRow(row);
                                setEdit(true);
                                setOpenSupportedDeviceForm(true);
                              }}
                            />
                          ) : null,
                          ability.can("delete", "Supported-Device") ? (
                            <GridActionsCellItem
                              key="delete"
                              sx={{
                                color: "error.main",
                              }}
                              icon={
                                <Iconify
                                  color="error.main"
                                  icon="eva:trash-2-outline"
                                />
                              }
                              label="Delete"
                              showInMenu
                              onClick={() => {
                                setOpenRow(row);
                                setOpenDeleteConfirmation(true);
                              }}
                            />
                          ) : null,
                        ].filter((action) => action),
                    }
                  : null
              }
            />
          </Scrollbar>
        </Card>
      </Container>

      {ability.can("delete", "Supported-Device") && (
        <CustomDeleteConfirmation
          open={openDeleteConfirmation}
          onClose={() => setOpenDeleteConfirmation(false)}
          tableName="Supported device"
          deleteFunc={deleteSupportedDevice}
          nameKey="deviceName"
          nameTitle="name"
          row={openRow}
          isLoading={isDeleting}
        />
      )}
    </Protected>
  );
}